//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import UserGroupDataService from '../../utils/UserGroupDataService';
import AiDataService from '../../utils/AiDataService';
import { useUserAuth } from '../../UserAuth';
import { UserGroupDto } from './types';

const MASKED_API_KEY = '••••••••';

// Helper function to detect if an API key is masked/encrypted
const isMaskedApiKey = (key: string): boolean => {
  if (!key) return false;
  // Check for bullet point masking (••••••••)
  if (key === MASKED_API_KEY) return true;
  // Check for asterisk masking patterns (sk-***AwA, *****, etc.)
  if (key.includes('***')) return true;
  // Check for other common masking patterns
  if (/^\*+$/.test(key)) return true;
  return false;
};

interface OverrideConfigModalProps {
  isOpen: boolean;
  teamspaceId: string;
  tenantBaseUrl: string;
  getUserContext: () => any;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Which integration to edit the override for.
   *
   * Required: a user may reach several integrations through different groups, and the
   * "active path" is only one of them. A caller that left this out used to fall back to
   * the active path and quietly edit the wrong integration's override, so there is no
   * fallback any more.
   */
  integrationName: string;
  /**
   * Which group's override this is.
   *
   * Overrides are per group, and each group decides whether it permits them. A user can reach
   * one integration through several groups, so the integration name alone does not say which
   * override is meant - the row being edited names its group. Used for both reading the
   * current configuration and saving, so the two cannot disagree.
   */
  groupId: string;
}

interface IntegrationData {
  id?: string;
  provider: string;
  name?: string;
  integrationName?: string;
}

interface ConfigSource {
  source: 'TEAMSPACE_DEFAULT' | 'GROUP_CONFIGURATION' | 'USER_OVERRIDE';
  provider: string;
  name?: string; // Integration name from /effective API
  integrationName?: string; // Alias for compatibility
  apiKey: string;
  model?: string;
  apiEndpoint?: string;
}

const OverrideConfigModal: React.FC<OverrideConfigModalProps> = ({
  isOpen,
  teamspaceId,
  tenantBaseUrl,
  getUserContext,
  onClose,
  onSuccess,
  integrationName: targetIntegrationName,
  groupId: targetGroupId,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentGroup, setCurrentGroup] = useState<UserGroupDto | null>(null);
  const [configSource, setConfigSource] = useState<ConfigSource | null>(null);
  const [integrationData, setIntegrationData] =
    useState<IntegrationData | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');

  // Track original values to detect changes
  const [originalApiKey, setOriginalApiKey] = useState('');
  const [originalModel, setOriginalModel] = useState('');
  const [originalApiEndpoint, setOriginalApiEndpoint] = useState('');

  // Model fetching state
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [user, isAuthenticated]);

  const aiDataService = useMemo(
    () =>
      new AiDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  useEffect(() => {
    if (!isOpen || !teamspaceId) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Read the named integration through the named group (masked API keys, like
        // /effective). Never the user's "active path": that is a different integration for
        // anyone in several groups.
        const dataService = UserGroupDataService.getInstance(
          tenantBaseUrl,
          getUserContext,
        );
        const configData = await dataService.getIntegrationConfig(
          teamspaceId,
          targetIntegrationName,
          targetGroupId,
        );

        if (!configData) {
          setError(t('userGroups.noIntegrationForOverride'));
          return;
        }

        // Set current group info from config data
        if (configData.sourceGroupName) {
          setCurrentGroup({
            name: configData.sourceGroupName,
            allowUserOverride: configData.allowUserOverride,
          } as UserGroupDto);
        } else {
          setCurrentGroup(null);
        }

        // Set config source with masked data
        setConfigSource({
          source: configData.source,
          provider: configData.configuration.provider,
          name: configData.integrationName,
          integrationName: configData.integrationName,
          apiKey: configData.configuration.apiKey || '',
          model: configData.configuration.model || '',
          apiEndpoint: configData.configuration.apiEndpoint || '',
        });

        // Set integration data for model fetching
        const integrationId =
          configData.integrationId || configData.configuration?.integrationId;
        setIntegrationData({
          id: integrationId,
          provider: configData.configuration.provider,
          name: configData.integrationName,
          integrationName: configData.integrationName,
        });

        // Set form fields with current values (already masked from API)
        const fetchedApiKey = configData.configuration.apiKey || '';
        const fetchedModel = configData.configuration.model || '';
        const fetchedApiEndpoint = configData.configuration.apiEndpoint || '';

        setApiKey(fetchedApiKey);
        setModel(fetchedModel);
        setApiEndpoint(fetchedApiEndpoint);

        // Store original values for change detection
        setOriginalApiKey(fetchedApiKey);
        setOriginalModel(fetchedModel);
        setOriginalApiEndpoint(fetchedApiEndpoint);
      } catch (err: any) {
        console.error('Error fetching override config data:', err);
        setError(err.message || t('userGroups.errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [
    isOpen,
    teamspaceId,
    tenantBaseUrl,
    targetIntegrationName,
    targetGroupId,
    t,
  ]);

  // Model discovery: 400ms debounce, keyed on provider + apiKey + apiEndpoint
  useEffect(() => {
    if (!integrationData) {
      return;
    }

    const provider = integrationData.provider;

    // Skip model fetching for Azure providers (they use deployment names)
    if (provider === 'azure-openai' || provider === 'azure-openai-gateway') {
      setModelOptions([]);
      setModelsError(null);
      return;
    }

    let cancelled = false;
    const key = (apiKey || '').trim();
    const endpoint = (apiEndpoint || '').trim() || undefined;

    const applyModels = (data: { models?: string[] }) => {
      if (cancelled) return;
      setModelsError(null);
      setModelOptions(Array.isArray(data.models) ? data.models : []);
    };

    const blockWithError = (message: string) => {
      if (cancelled) return;
      setModelOptions([]);
      setModelsError(message);
    };

    const fetchCatalogue = () => {
      const baseUrl = (tenantBaseUrl || '').replace(/\/$/, '');
      const url = `${baseUrl}/analyticsai/rest/models?provider=${encodeURIComponent(provider)}`;
      setModelsLoading(true);
      fetch(url)
        .then((res) => (res.ok ? res.json() : { models: [] }))
        .then(applyModels)
        .catch(() => {
          if (!cancelled) setModelOptions([]);
        })
        .finally(() => {
          if (!cancelled) setModelsLoading(false);
        });
    };

    const handle = setTimeout(() => {
      // If we have an integrationId, always use POST /api/models.
      // The backend uses the stored key for masked sentinel, or validates the supplied key.
      if (
        integrationData.id &&
        typeof aiDataService?.getModelsForKey === 'function'
      ) {
        setModelsLoading(true);
        const keyForFetch = isMaskedApiKey(key) ? '******' : key;
        aiDataService
          .getModelsForKey(
            provider,
            keyForFetch,
            endpoint,
            teamspaceId,
            integrationData.id,
          )
          .then((data) => {
            if (data?.ok === false) {
              blockWithError(
                data?.error?.message ||
                  'Unable to fetch models. Please verify the API key and endpoint.',
              );
            } else {
              applyModels(data);
            }
          })
          .catch(() =>
            blockWithError(
              'Unable to fetch models. Please verify the API key and endpoint.',
            ),
          )
          .finally(() => {
            if (!cancelled) setModelsLoading(false);
          });
      } else if (
        key &&
        !isMaskedApiKey(key) &&
        typeof aiDataService?.getModelsForKey === 'function'
      ) {
        // No integrationId but have a real API key - validate it
        setModelsLoading(true);
        aiDataService
          .getModelsForKey(provider, key, endpoint, teamspaceId, undefined)
          .then((data) => {
            if (data?.ok === false) {
              blockWithError(
                data?.error?.message ||
                  'Unable to fetch models. Please verify the API key and endpoint.',
              );
            } else {
              applyModels(data);
            }
          })
          .catch(() =>
            blockWithError(
              'Unable to fetch models. Please verify the API key and endpoint.',
            ),
          )
          .finally(() => {
            if (!cancelled) setModelsLoading(false);
          });
      } else {
        // No integrationId and no real key - fetch catalog as fallback
        setModelsError(null);
        fetchCatalogue();
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line
  }, [integrationData, apiKey, apiEndpoint, teamspaceId]);

  const handleSave = async () => {
    // Only save if user has made changes and has permission
    if (!currentGroup?.allowUserOverride) {
      return;
    }

    const integrationName = configSource?.integrationName || configSource?.name;
    if (!integrationName) {
      setError('No integration available to override');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Build override fields
      const overrideFields: any = {};

      // Only include fields that have been changed (not masked)
      if (apiKey !== originalApiKey && apiKey !== MASKED_API_KEY) {
        // Validate API key is not empty or whitespace
        if (!apiKey || !apiKey.trim()) {
          setError(t('userGroups.errors.apiKeyRequired'));
          setSaving(false);
          return;
        }
        overrideFields.apiKey = apiKey;
      }
      if (model !== originalModel) {
        overrideFields.model = model;
      }
      if (apiEndpoint !== originalApiEndpoint) {
        overrideFields.apiEndpoint = apiEndpoint;
      }

      // Save user override using UserGroupDataService
      const dataService = UserGroupDataService.getInstance(
        tenantBaseUrl,
        getUserContext,
      );
      await dataService.createUserOverride(
        teamspaceId,
        integrationName,
        overrideFields,
        targetGroupId,
      );

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('Error saving override:', err);
      setError(err.message || t('userGroups.errors.overrideFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const canOverride = currentGroup?.allowUserOverride ?? false;
  const isTeamspaceDefault = configSource?.source === 'TEAMSPACE_DEFAULT';

  // Check if user has made actual changes from the original values
  const hasChanges =
    (apiKey !== originalApiKey && apiKey !== MASKED_API_KEY) ||
    model !== originalModel ||
    apiEndpoint !== originalApiEndpoint;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="override-config-modal"
        className="sm:max-w-[500px]"
      >
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('userGroups.overrideConfig')}
          </h2>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            {t('userGroups.loading')}
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        ) : !currentGroup ? (
          <div className="rounded-md bg-blue-50 p-4 dark:bg-blue-900/30">
            <p className="text-sm text-blue-900 dark:text-blue-300">
              <strong>{t('userGroups.notPartOfGroup')}</strong>
            </p>
            <p className="mt-2 text-sm text-blue-800 dark:text-blue-300">
              {t('userGroups.usingTeamspaceDefault')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Group */}
            <div>
              <Label className="mb-2">{t('userGroups.currentGroup')}</Label>
              <div
                className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-[#393939] dark:text-gray-300"
                data-testid="override-config-modal-current-group"
              >
                {currentGroup.name}
              </div>
            </div>

            {/* Configuration Source */}
            <div>
              <Label className="mb-2">
                {t('userGroups.configurationSource')}
              </Label>
              <div
                className="rounded-md border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-[#393939] dark:text-gray-300"
                data-testid="override-config-modal-config-source"
              >
                {configSource?.source === 'USER_OVERRIDE' &&
                  t('userGroups.yourPersonalOverride')}
                {configSource?.source === 'GROUP_CONFIGURATION' &&
                  t('userGroups.groupSource', { groupName: currentGroup.name })}
                {configSource?.source === 'TEAMSPACE_DEFAULT' &&
                  t('userGroups.teamspaceDefaultSource')}
              </div>
            </div>

            {/* Warning if teamspace default */}
            {isTeamspaceDefault && (
              <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                {t('userGroups.teamspaceDefaultWarning')}
              </div>
            )}

            {/* Warning if override disabled */}
            {!canOverride && !isTeamspaceDefault && (
              <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                ⚠️ {t('userGroups.overrideDisabled')}
              </div>
            )}

            {/* Provider (read-only) */}
            <div>
              <Label className="mb-2">{t('userGroups.provider')}</Label>
              <Input
                value={configSource?.provider || ''}
                disabled
                className="bg-gray-50 dark:bg-[#393939]"
                data-testid="override-config-modal-provider"
              />
            </div>

            {/* API Key */}
            <div>
              <Label htmlFor="api-key" className="mb-2">
                {t('userGroups.apiKey')}
              </Label>
              <Input
                id="api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={!canOverride || isTeamspaceDefault}
                placeholder={t('userGroups.enterNewApiKey')}
                className={
                  !canOverride || isTeamspaceDefault
                    ? 'bg-gray-50 dark:bg-[#393939]'
                    : ''
                }
                data-testid="override-config-modal-api-key"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isMaskedApiKey(apiKey)
                  ? t('userGroups.currentKeyMasked')
                  : t('userGroups.leaveMaskedToKeep')}
              </p>
            </div>

            {/* Model */}
            <div>
              <Label htmlFor="model" className="mb-2 flex items-center gap-2">
                {t('userGroups.model')}
                {modelsLoading && (
                  <span
                    data-testid="model-loading-spinner"
                    role="status"
                    aria-label={t('loading-models')}
                    className="inline-block size-4 animate-spin rounded-full border-2 border-brand-interactive border-t-transparent dark:border-brand-blue dark:border-t-transparent"
                  />
                )}
              </Label>
              {configSource?.provider === 'azure-openai' ||
              configSource?.provider === 'azure-openai-gateway' ? (
                <Input
                  id="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  disabled={!canOverride || isTeamspaceDefault}
                  placeholder={t('userGroups.modelPlaceholder')}
                  className={
                    !canOverride || isTeamspaceDefault
                      ? 'bg-gray-50 dark:bg-[#393939]'
                      : ''
                  }
                  data-testid="override-config-modal-model"
                />
              ) : (
                <Autocomplete
                  freeSolo
                  clearOnEscape
                  disablePortal
                  disabled={
                    !canOverride ||
                    isTeamspaceDefault ||
                    modelsLoading ||
                    !!modelsError
                  }
                  options={modelOptions}
                  value={model || ''}
                  onChange={(_, newValue) => setModel(newValue ?? '')}
                  onInputChange={(_, newInputValue) =>
                    setModel(newInputValue || '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        id: 'model',
                        'data-testid': 'override-config-modal-model',
                      }}
                      placeholder={t('userGroups.modelPlaceholder')}
                      size="small"
                    />
                  )}
                />
              )}
              {modelsError && (
                <p className="mt-1 text-sm text-red-500">{modelsError}</p>
              )}
            </div>

            {/* API Endpoint */}
            <div>
              <Label htmlFor="api-endpoint" className="mb-2">
                {t('userGroups.apiEndpoint')}
              </Label>
              <Input
                id="api-endpoint"
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
                disabled={!canOverride || isTeamspaceDefault}
                placeholder={t('userGroups.apiEndpointPlaceholder')}
                className={
                  !canOverride || isTeamspaceDefault
                    ? 'bg-gray-50 dark:bg-[#393939]'
                    : ''
                }
                data-testid="override-config-modal-api-endpoint"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            data-testid="override-config-modal-cancel-button"
          >
            {t('userGroups.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              loading ||
              !canOverride ||
              isTeamspaceDefault ||
              !hasChanges ||
              !currentGroup
            }
            data-testid="override-config-modal-save-button"
          >
            {saving ? t('userGroups.saving') : t('userGroups.saveOverride')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OverrideConfigModal;
